'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Phone, PhoneCall, MicOff, WifiOff, Loader2 } from 'lucide-react';
import { useWebphoneContext } from '@/hooks/voice/webphone-provider';
import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS_UPDATED_EVENT } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type { WebphoneState } from '@/hooks/voice/webphone-types';
import { WebphonePanelIdle } from './webphone-panel-idle';
import { WebphonePanelActive } from './webphone-panel-active';
import { IncomingCallModal } from './incoming-call-modal';
import { WebphoneTransferDialog } from './webphone-transfer-dialog';
import { useRingtone } from './use-ringtone';
import { useVoiceSocket } from '@/hooks/use-voice-socket';
import { toDialableBr } from '@/hooks/voice/phone-format';
import { toast } from 'sonner';

// Persistência da posição arrastada (pixels do canto bottom-right). Sobrevive
// reload. Reset: localStorage.removeItem('gk-webphone-pos').
const POS_STORAGE_KEY = 'gk-webphone-pos';
const DEFAULT_POS = { bottom: 24, right: 24 }; // bottom-6 right-6 em px
const DRAG_THRESHOLD_PX = 5;
const BUBBLE_SIZE = 56; // h-14 w-14

function loadStoredPos(): { bottom: number; right: number } {
  if (typeof window === 'undefined') return DEFAULT_POS;
  try {
    const raw = localStorage.getItem(POS_STORAGE_KEY);
    if (!raw) return DEFAULT_POS;
    const parsed = JSON.parse(raw) as { bottom?: number; right?: number };
    if (typeof parsed.bottom !== 'number' || typeof parsed.right !== 'number') {
      return DEFAULT_POS;
    }
    return parsed as { bottom: number; right: number };
  } catch {
    return DEFAULT_POS;
  }
}

function clampToViewport(p: { bottom: number; right: number }) {
  if (typeof window === 'undefined') return p;
  const maxRight = Math.max(0, window.innerWidth - BUBBLE_SIZE);
  const maxBottom = Math.max(0, window.innerHeight - BUBBLE_SIZE);
  return {
    bottom: Math.min(Math.max(0, p.bottom), maxBottom),
    right: Math.min(Math.max(0, p.right), maxRight),
  };
}

export function WebphoneSticky() {
  const wp = useWebphoneContext();
  const [expanded, setExpanded] = useState(false);
  const [hasPerms, setHasPerms] = useState(false);
  // #112: gate do socket /voice — alinhado a permissao que o VoiceGateway exige
  // (telefonia.dashboard.ver). Sem isso o webphone abria /voice em TODA aba de
  // qualquer usuario logado num tenant com telefonia ligada (storm user=undefined).
  const [canConnectVoice, setCanConnectVoice] = useState(false);
  const [pos, setPos] = useState<{ bottom: number; right: number }>(DEFAULT_POS);
  const [agentName, setAgentName] = useState<string | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  // unique_id (motor) da chamada ATIVA deste agente. Antes era adivinhado
  // casando o ramal contra a lista de chamadas ativas — DAVA 404 quando o
  // CDR ainda não tinha esse ramal preenchido. Contrato DEFINITIVO do motor
  // (2026-06-13): o uid vem do evento ANCHOR `voice.bridged`. Fonte única.
  // uid (motor) + protocolo (display_id da conversa) da chamada ATIVA deste agente,
  // num objeto só pra o clear no hangup ser atômico (o callback do socket tem deps
  // [], então ler state solto seria stale). Fonte única: evento ANCHOR voice.bridged
  // (inbound) / voice.ringing (outbound). O protocolo o agente vê durante a ligação.
  const [activeCall, setActiveCall] = useState<{ uid: string; protocolo: string | null } | null>(null);
  const activeUniqueId = activeCall?.uid ?? null;
  // wp.extension muda de render; ref evita re-subscrever o socket a cada toggle.
  const extensionRef = useRef<string | null>(wp.extension);
  extensionRef.current = wp.extension;
  // Ref do webphone pro listener global (click-to-call) ler sempre o estado
  // atual sem re-registrar o event listener a cada render.
  const wpRef = useRef(wp);
  wpRef.current = wp;

  // Ringback de saida: o browser nao gera o "puuu...." sozinho ao receber o 180
  // Ringing (softphone/telefone IP geram nativamente). Toca enquanto a chamada de
  // SAIDA esta discando/chamando e ainda nao foi atendida. Hook antes dos returns
  // condicionais abaixo (regra dos hooks). Chamada RECEBIDA usa o ringtone do
  // incoming-call-modal, entao aqui filtramos por direction outbound.
  const isOutboundRinging =
    wp.call?.direction === 'outbound' &&
    (wp.state === 'calling' || wp.state === 'ringing');
  useRingtone(isOutboundRinging, 'ringback');

  const { connected: voiceConnected } = useVoiceSocket(
    useCallback((evt) => {
      const data = evt.data as {
        unique_id?: string;
        direction?: string;
        answered_by_extension?: string;
        protocolo?: string | null;
      };
      const uid = data.unique_id;
      if (!uid) return;
      const myExt = extensionRef.current;

      switch (evt.event) {
        case 'voice.bridged':
          // ANCHOR inbound: o ramal que atendeu sou eu → esse é o meu uid.
          if (myExt && data.answered_by_extension === myExt) {
            setActiveCall({ uid, protocolo: data.protocolo ?? null });
          }
          break;
        case 'voice.dialing':
        case 'voice.ringing':
          // Outbound da minha própria origem — pego o uid já no discar/tocar.
          if (data.direction === 'outbound') {
            setActiveCall({ uid, protocolo: data.protocolo ?? null });
          }
          break;
        case 'voice.hangup':
        case 'voice.transferred':
          // Encerrou/transferiu A MINHA chamada ativa → limpa (uid + protocolo).
          setActiveCall((prev) => (prev?.uid === uid ? null : prev));
          break;
        default:
          break;
      }
    }, []),
    // #112: permissao (telefonia.dashboard.ver) E #178: socket só abre com o
    // SIP de pé — creds OK implica ramal vinculado. Agente sem ramal (ou motor
    // fora) não gera nem 1 handshake /voice pelo sticky; os eventos que este
    // socket consome são da PRÓPRIA chamada, impossível sem registro SIP.
    canConnectVoice &&
      (wp.state === 'registered' ||
        wp.state === 'calling' ||
        wp.state === 'ringing' ||
        wp.state === 'in_call'),
  );

  // Recovery: se o socket /voice caiu e reconectou DURANTE uma chamada, o evento
  // ANCHOR `voice.bridged` pode ter se perdido na janela — fica sem activeUniqueId
  // e o botão Transferir trava cinza mesmo com o agente em ligação. Ao reconectar
  // (e só se ainda estou em chamada sem uid), re-descubro a chamada ativa deste
  // ramal via GET /telefonia/chamadas/ativa. Falha silenciosa: o recovery é
  // best-effort, os eventos do socket continuam sendo a fonte primária.
  useEffect(() => {
    if (!voiceConnected || activeUniqueId || wp.state !== 'in_call') return;
    api
      .request<{
        chamada: {
          unique_id: string;
          answered_by_extension: string;
          protocolo: string | null;
        } | null;
      }>('/telefonia/chamadas/ativa')
      .then(({ chamada }) => {
        if (chamada && chamada.answered_by_extension === extensionRef.current) {
          setActiveCall({ uid: chamada.unique_id, protocolo: chamada.protocolo ?? null });
        }
      })
      .catch(() => {});
  }, [voiceConnected, activeUniqueId, wp.state]);

  useEffect(() => {
    // #112 hardening: reavalia as permissões no mount E quando elas chegam/mudam
    // (boot async, /auth/permissions repovoando o storage, admin concedendo
    // telefonia.* na sessão, ou outra aba renovando). Sem isto, um telefonista que
    // só recebe a permissão DEPOIS do mount ficaria sem webphone até dar F5.
    const applyPerms = () => {
      setHasPerms(hasPermission('telefonia.chamadas.originar'));
      setCanConnectVoice(hasPermission('telefonia.dashboard.ver')); // #112
    };
    applyPerms();
    setPos(clampToViewport(loadStoredPos()));
    setAgentName(api.getUser()?.name ?? null);
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, applyPerms);
    window.addEventListener('storage', applyPerms);
    return () => {
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, applyPerms);
      window.removeEventListener('storage', applyPerms);
    };
  }, []);

  // Auto-connect quando permissao presente
  useEffect(() => {
    if (hasPerms && wp.state === 'disconnected') {
      void wp.connect();
    }
  }, [hasPerms, wp]);

  // Click-to-call: outras telas (ex: ficha do cliente no Cockpit) disparam um
  // CustomEvent `gk:voice-dial` com o número. O webphone — única instância que
  // mantém o registro SIP — escuta e disca. Sem acoplar essas telas ao hook.
  useEffect(() => {
    function handleVoiceDial(e: Event) {
      // Normaliza pro formato da rota de saída (0 + DDD + número) — o ERP manda
      // em formatos mistos (com/sem 55, com máscara). Ver toDialableBr.
      const number = toDialableBr(
        (e as CustomEvent<{ number?: string }>).detail?.number ?? '',
      );
      if (!number) return;
      const w = wpRef.current;
      if (w.state !== 'registered') {
        toast.error(
          'Webphone não está registrado. Abra o webphone e aguarde conectar para ligar.',
        );
        setExpanded(true);
        return;
      }
      setExpanded(true);
      void w.dial(number);
    }
    window.addEventListener('gk:voice-dial', handleVoiceDial as EventListener);
    return () =>
      window.removeEventListener('gk:voice-dial', handleVoiceDial as EventListener);
  }, []);

  if (!hasPerms) return null;
  // #178: agente sem ramal vinculado — nem todo agente atende telefone; o
  // sticky some por completo (decisão de produto 10/07). Admin vinculou
  // depois? O próximo page-load re-tenta (o probe é o próprio connect).
  if (wp.error?.code === 'no_extension') return null;

  const isRinging = wp.state === 'ringing';
  const isIncoming = isRinging && wp.call?.direction === 'inbound';
  // outbound ringing tambem mostra panel de chamada (modal so para inbound)
  const inCall =
    wp.state === 'in_call' ||
    wp.state === 'calling' ||
    (isRinging && !isIncoming);

  // Modal incoming aparece sempre que tem chamada inbound tocando.
  if (isIncoming && wp.call) {
    return (
      <IncomingCallModal
        call={wp.call}
        onAccept={() => {
          wp.acceptIncoming();
          setExpanded(true);
        }}
        onReject={() => wp.rejectIncoming()}
      />
    );
  }

  return (
    <div
      className={cn('fixed z-40', 'transition-all duration-200 ease-out')}
      style={{ bottom: `${pos.bottom}px`, right: `${pos.right}px` }}
    >
      {expanded ? (
        inCall && wp.call ? (
          <>
            <WebphonePanelActive
              call={wp.call}
              state={wp.state}
              protocolo={activeCall?.protocolo ?? null}
              onMinimize={() => setExpanded(false)}
              onHangup={wp.hangup}
              onToggleMute={wp.toggleMute}
              onToggleHold={wp.toggleHold}
              onSendDtmf={wp.sendDtmf}
              onTransfer={() => setTransferOpen(true)}
            />
            <WebphoneTransferDialog
              open={transferOpen}
              onOpenChange={setTransferOpen}
              callUid={activeUniqueId}
              ownExtension={wp.extension}
              onTransferred={() => {
                // Blind transfer larga a chamada na hora — encerra estado local.
                setActiveCall(null);
                wp.hangup();
                setExpanded(false);
              }}
            />
          </>
        ) : (
          <WebphonePanelIdle
            state={wp.state}
            error={wp.error}
            extension={wp.extension}
            agentName={agentName}
            onMinimize={() => setExpanded(false)}
            onDial={(num) => void wp.dial(num)}
            onSimulateIncoming={wp.simulateIncoming}
            onReconnect={() => void wp.connect()}
          />
        )
      ) : (
        <WebphoneBubble
          state={wp.state}
          inCall={inCall}
          muted={wp.call?.muted ?? false}
          pos={pos}
          onClick={() => setExpanded(true)}
          onPosChange={(next) => {
            const clamped = clampToViewport(next);
            setPos(clamped);
            try {
              localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(clamped));
            } catch {
              // localStorage cheio/desativado — perde só a persistência, não trava UI
            }
          }}
        />
      )}
    </div>
  );
}

interface BubbleProps {
  state: WebphoneState;
  inCall: boolean;
  muted: boolean;
  pos: { bottom: number; right: number };
  onClick: () => void;
  onPosChange: (next: { bottom: number; right: number }) => void;
}

function WebphoneBubble({ state, inCall, muted, pos, onClick, onPosChange }: BubbleProps) {
  // Drag com Pointer Events (cobre mouse + touch). Capturamos no document
  // pra continuar recebendo o move mesmo se o cursor sair do bubble durante
  // o arrasto. Threshold de 5px distingue click (expandir painel) de drag.
  const draggingRef = useRef<{
    startX: number;
    startY: number;
    startPos: typeof pos;
    pointerId: number;
  } | null>(null);
  const movedRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      const d = draggingRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!movedRef.current && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
        movedRef.current = true;
      }
      // bottom/right crescem pra ESQUERDA/CIMA, daí inverter sinal do dx/dy.
      onPosChange({ bottom: d.startPos.bottom - dy, right: d.startPos.right - dx });
    },
    [onPosChange],
  );

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      const d = draggingRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      try {
        buttonRef.current?.releasePointerCapture?.(d.pointerId);
      } catch {
        // silencioso — algumas situações o pointer já não está mais capturado
      }
      draggingRef.current = null;
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
    },
    [onPointerMove],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return; // só left button / primary touch
    // setPointerCapture garante que continuamos recebendo os eventos do mesmo
    // ponteiro mesmo que ele saia do bubble durante o arrasto.
    try {
      buttonRef.current?.setPointerCapture?.(e.pointerId);
    } catch {
      // navegador antigo — drag funciona via document listeners
    }
    movedRef.current = false;
    draggingRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPos: pos,
      pointerId: e.pointerId,
    };
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
  };

  const handleClick = () => {
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    onClick();
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);
  const dotColor =
    state === 'registered'
      ? 'bg-emerald-500'
      : state === 'connecting'
        ? 'bg-amber-500 animate-pulse'
        : state === 'failed' || state === 'disconnected'
          ? 'bg-zinc-400'
          : 'bg-blue-500 animate-pulse';

  const Icon =
    state === 'disconnected' || state === 'failed'
      ? WifiOff
      : state === 'connecting'
        ? Loader2
        : inCall
          ? muted
            ? MicOff
            : PhoneCall
          : Phone;

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      onPointerDown={onPointerDown}
      // touch-action: none impede o browser de tomar conta do gesto pra
      // scroll/zoom no mobile — sem isso o pointer events não disparam direito.
      style={{ touchAction: 'none' }}
      className={cn(
        'group relative flex h-14 w-14 items-center justify-center rounded-full shadow-lg',
        'border-2 transition-all hover:scale-105 active:scale-95',
        'cursor-grab active:cursor-grabbing select-none',
        inCall
          ? 'bg-primary text-primary-foreground border-primary/50 shadow-primary/30 animate-pulse'
          : state === 'registered'
            ? 'bg-card text-foreground border-border'
            : 'bg-muted text-muted-foreground border-border',
      )}
      aria-label="Webphone (arraste pra mover)"
      title={`Webphone — ${state} (arraste pra mover)`}
    >
      <Icon className={cn('h-6 w-6', state === 'connecting' && 'animate-spin')} />
      <span
        className={cn(
          'absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-background',
          dotColor,
        )}
      />
    </button>
  );
}
